import { el, getApiBase, getStorage } from './utils';
import type { EmbedPoll, VoteMap } from './utils';
import { embedStyles } from './styles';
import { renderOptionPoll } from './render-what';
import { renderTimePoll } from './render-when';
import { renderVoteForm } from './render-form';

function init() {
	// Inject Google Fonts into host page (fonts don't load inside Shadow DOM)
	injectFonts();

	// Process existing elements
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', processAll);
	} else {
		processAll();
	}

	// Watch for dynamically added elements (SPA support)
	const observer = new MutationObserver((mutations) => {
		for (const mutation of mutations) {
			for (const node of mutation.addedNodes) {
				if (node instanceof HTMLElement) {
					if (node.hasAttribute('data-dtelection')) {
						processElement(node);
					}
					// Also check descendants
					const children = node.querySelectorAll('[data-dtelection]');
					for (const child of children) {
						processElement(child as HTMLElement);
					}
				}
			}
		}
	});

	observer.observe(document.body || document.documentElement, {
		childList: true,
		subtree: true
	});
}

function injectFonts() {
	if (document.querySelector('link[data-dte-fonts]')) return;
	const link = document.createElement('link');
	link.rel = 'stylesheet';
	link.href = 'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Sora:wght@600;700&display=swap';
	link.setAttribute('data-dte-fonts', 'true');
	document.head.appendChild(link);
}

function processAll() {
	const containers = document.querySelectorAll('[data-dtelection]');
	for (const container of containers) {
		processElement(container as HTMLElement);
	}
}

const processed = new WeakSet<HTMLElement>();

function processElement(container: HTMLElement) {
	if (processed.has(container)) return;
	processed.add(container);

	const token = container.getAttribute('data-dtelection');
	if (!token) return;

	// Create Shadow DOM
	const shadow = container.attachShadow({ mode: 'open' });

	// Inject styles
	const style = document.createElement('style');
	style.textContent = embedStyles;
	shadow.appendChild(style);

	// Show loading state
	const wrapper = el('div', { class: 'dte-container' });
	shadow.appendChild(wrapper);
	showLoading(wrapper);

	// Fetch and render
	loadPoll(token, wrapper, shadow);
}

// Clear all children safely (no innerHTML)
function clearElement(element: HTMLElement | ShadowRoot) {
	while (element.lastChild) {
		element.removeChild(element.lastChild);
	}
}

function showLoading(container: HTMLElement) {
	clearElement(container);
	const loading = el('div', { class: 'dte-loading' }, [
		el('div', { class: 'dte-spinner' }),
		el('div', { class: 'dte-loading-text' }, ['Loading poll...'])
	]);
	container.appendChild(loading);
}

function showError(container: HTMLElement, icon: string, title: string, message: string) {
	clearElement(container);
	const error = el('div', { class: 'dte-error-state' }, [
		el('div', { class: 'dte-error-icon' }, [icon]),
		el('div', { class: 'dte-error-title' }, [title]),
		el('div', { class: 'dte-error-message' }, [message])
	]);
	container.appendChild(error);
}

async function loadPoll(token: string, wrapper: HTMLElement, shadow: ShadowRoot) {
	try {
		const apiBase = getApiBase();
		const res = await fetch(`${apiBase}/api/v1/embed/${token}`);

		if (!res.ok) {
			const data = await res.json().catch(() => ({}));
			const code = data?.error?.code || '';

			switch (res.status) {
				case 404:
					showError(wrapper, '🔍', 'Poll not found', 'This poll may have been deleted or the link is incorrect.');
					break;
				case 403:
					if (code === 'EMBED_DISABLED') {
						showError(wrapper, '🚫', 'Embedding disabled', 'This poll is not available for embedding.');
					} else if (code === 'DOMAIN_NOT_ALLOWED') {
						showError(wrapper, '🔒', 'Domain not allowed', 'This poll cannot be embedded on this site.');
					} else {
						showError(wrapper, '🚫', 'Access denied', data?.error?.message || 'Permission denied.');
					}
					break;
				case 429:
					showError(wrapper, '⏳', 'Too many requests', 'Please try again later.');
					break;
				default:
					showError(wrapper, '⚠️', 'Something went wrong', 'Could not load the poll. Please try again.');
			}
			return;
		}

		const poll: EmbedPoll = await res.json();
		renderPoll(token, poll, wrapper, shadow);
	} catch {
		showError(wrapper, '📡', 'Connection error', 'Could not reach the server. Check your internet connection.');
	}
}

function renderPoll(token: string, poll: EmbedPoll, wrapper: HTMLElement, shadow: ShadowRoot) {
	clearElement(wrapper);

	const storage = getStorage(token);
	const hasVoted = storage?.voted === true;
	const isClosed = poll.status === 'closed';
	const isReadOnly = hasVoted || isClosed;

	// Header
	const header = el('div', { class: 'dte-header' });

	const typeLabel = poll.poll_type === 'when' ? '📅 Find a time' : '📊 Make a choice';
	header.appendChild(el('div', { class: 'dte-type-badge' }, [typeLabel]));
	header.appendChild(el('div', { class: 'dte-title' }, [poll.title]));

	if (poll.description) {
		header.appendChild(el('div', { class: 'dte-description' }, [poll.description]));
	}

	if (poll.location_text) {
		header.appendChild(el('div', { class: 'dte-location' }, ['📍 ', poll.location_text]));
	}

	wrapper.appendChild(header);

	// Status banner
	if (isClosed) {
		wrapper.appendChild(el('div', { class: 'dte-banner dte-banner-closed' }, ['This poll is closed']));
	} else if (hasVoted) {
		wrapper.appendChild(el('div', { class: 'dte-banner dte-banner-voted' }, ['✓ You voted on this poll']));
	}

	// Vote grid
	const votes: VoteMap = {};
	const onVotesChange = () => {
		// Re-render the grid in place
		reRenderGrid(poll, votes, isReadOnly, wrapper, shadow);
	};

	let grid: HTMLElement;
	if (poll.poll_type === 'when') {
		grid = renderTimePoll(poll, votes, onVotesChange, isReadOnly);
	} else {
		grid = renderOptionPoll(poll, votes, onVotesChange, isReadOnly);
	}
	grid.setAttribute('data-dte-grid', 'true');
	wrapper.appendChild(grid);

	// Vote form (only if poll is open and user hasn't voted)
	if (!isReadOnly) {
		const form = renderVoteForm(poll, token, votes, () => {
			// On success: re-fetch and re-render
			showLoading(wrapper);
			loadPoll(token, wrapper, shadow);
		});
		form.setAttribute('data-dte-form', 'true');
		wrapper.appendChild(form);
	}

	// Footer / branding
	if (poll.show_branding) {
		const footer = el('div', { class: 'dte-footer' });
		const link = el('a', {
			class: 'dte-branding',
			href: 'https://dtelection.com?utm_source=embed&utm_medium=widget&utm_campaign=powered_by',
			target: '_blank',
			rel: 'noopener noreferrer'
		}, ['Powered by dtElection']);
		footer.appendChild(link);
		wrapper.appendChild(footer);
	}
}

function reRenderGrid(poll: EmbedPoll, votes: VoteMap, isReadOnly: boolean, wrapper: HTMLElement, shadow: ShadowRoot) {
	// Remove old grid
	const oldGrid = wrapper.querySelector('[data-dte-grid]');
	if (!oldGrid) return;

	const onVotesChange = () => {
		reRenderGrid(poll, votes, isReadOnly, wrapper, shadow);
	};

	let newGrid: HTMLElement;
	if (poll.poll_type === 'when') {
		newGrid = renderTimePoll(poll, votes, onVotesChange, isReadOnly);
	} else {
		newGrid = renderOptionPoll(poll, votes, onVotesChange, isReadOnly);
	}
	newGrid.setAttribute('data-dte-grid', 'true');
	oldGrid.replaceWith(newGrid);
}

// Entry point — MUST be at the end of the file.
// Vite's IIFE bundling converts function declarations to const assignments,
// which are not hoisted. Calling init() before its declaration causes
// "Cannot access 'X' before initialization" in the minified bundle.
if (!(window as unknown as Record<string, boolean>).__dteLoaded) {
	(window as unknown as Record<string, boolean>).__dteLoaded = true;
	init();
}
